import React, { useEffect, useState } from 'react';
import SimpleAnimatedCounter from '../components/SimpleAnimatedCounter';

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    customPosition: '',
    experience: '',
    resume: null
  });
  const [showCustomPosition, setShowCustomPosition] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({
    question: '',
    answer: '',
    userAnswer: '',
    isValid: false
  });

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-', '*'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer;
    let question;
    
    switch(operator) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        // Ensure no negative results
        const larger = Math.max(num1, num2);
        const smaller = Math.min(num1, num2);
        answer = larger - smaller;
        question = `${larger} - ${smaller}`;
        break;
      case '*':
        // Use smaller numbers for multiplication
        const smallNum1 = Math.floor(Math.random() * 5) + 1;
        const smallNum2 = Math.floor(Math.random() * 5) + 1;
        answer = smallNum1 * smallNum2;
        question = `${smallNum1} × ${smallNum2}`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
    }
    
    setCaptcha({
      question,
      answer: answer.toString(),
      userAnswer: '',
      isValid: false
    });
  };

  const jobVacancies = [
    {
      title: "Safety Officer",
      experience: "1 year minimum experience",
      description: "Ensure workplace safety compliance and implement safety protocols across renewable energy project sites.",
      icon: "🛡️",
      type: "Safety",
      location: "On-site"
    },
    {
      title: "Project Manager",
      experience: "2-5 years of experience in renewable energy projects related to wind turbine generator projects",
      description: "Lead and manage wind turbine generator projects from planning to execution, ensuring timely delivery and quality standards.",
      icon: "🎯",
      type: "Management",
      location: "Hybrid"
    },
    {
      title: "Project Coordinator",
      experience: "1-3 years of experience in renewable energy",
      description: "Coordinate project activities, manage timelines, and facilitate communication between stakeholders in renewable energy projects.",
      icon: "📋",
      type: "Coordination",
      location: "Office/Remote"
    },
    {
      title: "Civil Engineer",
      experience: "Minimum 1 year of experience in renewable energy",
      description: "Design and supervise civil engineering aspects of renewable energy projects including foundations and structural work.",
      icon: "🏗️",
      type: "Engineering",
      location: "On-site"
    },
    {
      title: "Electrical Engineer",
      experience: "1 year of experience with 11/33/66 kV line with all related work",
      description: "Handle electrical systems design, installation, and maintenance for high voltage transmission lines in renewable energy projects.",
      icon: "⚡",
      type: "Engineering",
      location: "On-site"
    },
    {
      title: "Document Controller",
      experience: "At least 1 year of experience",
      description: "Manage project documentation, maintain records, and ensure compliance with documentation standards.",
      icon: "📁",
      type: "Administration",
      location: "Office"
    },
    {
      title: "Renewable Energy Consultant",
      experience: "Minimum 1 year of experience",
      description: "Provide expert consultation on renewable energy solutions, feasibility studies, and project optimization.",
      icon: "💡",
      type: "Consulting",
      location: "Client-site"
    }
  ];

  useEffect(() => {
    document.title = 'Career - VReco Energy | Join Our Team';
    generateCaptcha();
    
    // Initialize scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);
    
    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach(el => observer.observe(el));

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'position') {
      setShowCustomPosition(value === 'Other');
      if (value !== 'Other') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          customPosition: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCaptchaChange = (e) => {
    const userAnswer = e.target.value;
    const isValid = userAnswer === captcha.answer;
    
    setCaptcha(prev => ({
      ...prev,
      userAnswer,
      isValid
    }));
  };

  const refreshCaptcha = () => {
    generateCaptcha();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf' || file.type.startsWith('application/vnd.openxmlformats-officedocument.wordprocessingml')) {
        setFormData(prev => ({
          ...prev,
          resume: file
        }));
      } else {
        showNotification('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captcha.isValid) {
      showNotification('Please solve the captcha correctly', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('customPosition', formData.customPosition);
      formDataToSend.append('experience', formData.experience);
      
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      const response = await fetch('/api/career', {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok) {
        showNotification(result.message, 'success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          customPosition: '',
          experience: '',
          resume: null
        });
        setShowCustomPosition(false);
        
        // Reset file input
        const fileInput = document.getElementById('resume');
        if (fileInput) fileInput.value = '';
        
        // Reset captcha
        generateCaptcha();
      } else {
        showNotification(result.error || 'Failed to submit application', 'error');
      }
    } catch (error) {
      console.error('Career form error:', error);
      showNotification('Network error. Please check your connection and try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: ${type === 'success' ? '#2E7D32' : '#d32f2f'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 10px;
      box-shadow: 0 5px 15px rgba(0,0,0,0.2);
      z-index: 10000;
      max-width: 400px;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      document.body.removeChild(notification);
    }, 5000);
  };

  return (
    <div className="career-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="hero-content fade-in">
            <h1>Join Our Mission</h1>
            <p>Build a sustainable future with renewable energy technology</p>
            <div className="career-stats">
              <div className="stat-item">
                <SimpleAnimatedCounter end={9} suffix="+" className="stat-number" />
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-item">
                <SimpleAnimatedCounter end={9} suffix="+" className="stat-number" />
                <span className="stat-label">Projects Completed</span>
              </div>
              <div className="stat-item">
                <SimpleAnimatedCounter end={7} className="stat-number" />
                <span className="stat-label">Open Positions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Vacancies */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Current Openings</h2>
          <p className="section-subtitle fade-in">Discover your next career opportunity in renewable energy</p>
          
          <div className="jobs-grid">
            {jobVacancies.map((job, index) => (
              <div key={index} className="job-card fade-in">
                <div className="job-header">
                  <div className="job-icon">{job.icon}</div>
                  <div className="job-meta">
                    <span className="job-type">{job.type}</span>
                    <span className="job-location">📍 {job.location}</span>
                  </div>
                </div>
                <h3 className="job-title">{job.title}</h3>
                <div className="job-requirements">
                  <span className="experience-tag">📊 {job.experience}</span>
                </div>
                <p className="job-description">{job.description}</p>
                <div className="job-actions">
                  <button 
                    className="apply-btn"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, position: job.title }));
                      setShowCustomPosition(false);
                      document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Other Positions Info */}
          <div className="other-positions-info fade-in">
            <div className="info-card">
              <div className="info-icon">🔍</div>
              <div className="info-content">
                <h3>Don't See Your Ideal Position?</h3>
                <p>
                  We're always looking for talented individuals to join our renewable energy mission. 
                  If you have skills that could contribute to our wind turbine, transmission line, or 
                  renewable energy projects, we'd love to hear from you!
                </p>
                <button 
                  className="apply-other-btn"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, position: 'Other' }));
                    setShowCustomPosition(true);
                    document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Apply for Other Position
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="section why-choose">
        <div className="container">
          <h2 className="section-title fade-in" style={{ color: 'white' }}>Why Join VReco Energy?</h2>
          <p className="section-subtitle fade-in" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Be part of India's leading renewable energy revolution
          </p>
          
          <div className="benefits-grid">
            <div className="benefit-card fade-in">
              <div className="benefit-icon">🌱</div>
              <h3>Sustainable Impact</h3>
              <p>Contribute to India's clean energy goals and make a lasting environmental impact through innovative renewable energy solutions.</p>
            </div>
            
            <div className="benefit-card fade-in">
              <div className="benefit-icon">🚀</div>
              <h3>Career Growth</h3>
              <p>Accelerate your professional journey with continuous learning opportunities, certifications, and leadership development programs.</p>
            </div>
            
            <div className="benefit-card fade-in">
              <div className="benefit-icon">🤝</div>
              <h3>Expert Team</h3>
              <p>Collaborate with industry veterans and renewable energy specialists with over 9 years of combined experience.</p>
            </div>
            
            <div className="benefit-card fade-in">
              <div className="benefit-icon">💼</div>
              <h3>Cutting-Edge Projects</h3>
              <p>Work on state-of-the-art wind turbine installations, transmission lines, and renewable energy infrastructure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="section" id="application-form">
        <div className="container">
          <div className="application-wrapper">
            <div className="form-header fade-in">
              <h2 className="section-title">Ready to Join Us?</h2>
              <p className="section-subtitle">Submit your application and let's build the future together</p>
              <div className="contact-highlight">
                <span className="email-icon">✉️</span>
                <span>Applications sent to: <strong>contact@vr-eco.com</strong></span>
              </div>
            </div>
            
            <div className="application-form-card fade-in">
              
              <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <span className="label-icon">👤</span>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">
                      <span className="label-icon">✉️</span>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">
                      <span className="label-icon">📞</span>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="experience">
                      <span className="label-icon">⏱️</span>
                      Years of Experience *
                    </label>
                    <input
                      type="text"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      placeholder="e.g., 2 years"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="position">
                    <span className="label-icon">💼</span>
                    Position Applied For *
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Position</option>
                    {jobVacancies.map((job, index) => (
                      <option key={index} value={job.title}>
                        {job.icon} {job.title}
                      </option>
                    ))}
                    <option value="Other">🔍 Other Position (Not Listed Above)</option>
                  </select>
                </div>
                
                {showCustomPosition && (
                  <div className="form-group custom-position-group">
                    <label htmlFor="customPosition">
                      <span className="label-icon">✏️</span>
                      Specify Position *
                    </label>
                    <input
                      type="text"
                      id="customPosition"
                      name="customPosition"
                      value={formData.customPosition}
                      onChange={handleInputChange}
                      placeholder="Enter the position you're interested in"
                      required={showCustomPosition}
                    />
                    <small className="field-hint">
                      Tell us about the specific role you'd like to apply for, even if it's not currently listed in our openings.
                    </small>
                  </div>
                )}
                
                <div className="form-group file-upload-group">
                  <label htmlFor="resume">
                    <span className="label-icon">📄</span>
                    Upload Resume (PDF/Word) - Optional
                  </label>
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                    />
                    <div className="file-upload-display">
                      {formData.resume ? (
                        <div className="file-selected">
                          <span className="file-icon">📎</span>
                          <span className="file-name">{formData.resume.name}</span>
                          <span className="file-size">
                            ({Math.round(formData.resume.size / 1024)} KB)
                          </span>
                        </div>
                      ) : (
                        <div className="file-placeholder">
                          <span className="upload-icon">⬆️</span>
                          <span>Click to select your resume (Optional)</span>
                          <small>PDF, DOC, or DOCX files only</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Captcha Verification */}
                <div className="form-group" style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', 
                  padding: '1.5rem', 
                  borderRadius: '15px',
                  border: '2px solid #1565C0',
                  marginTop: '1.5rem'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    marginBottom: '1rem',
                    color: '#1565C0',
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    <span className="label-icon">🔒</span>
                    Security Verification *
                  </label>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem', 
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '1rem 1.5rem',
                      borderRadius: '10px',
                      border: '2px solid #e9ecef',
                      fontFamily: 'monospace',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#1565C0',
                      minWidth: '120px',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      {captcha.question} = ?
                    </div>
                    
                    <input
                      type="number"
                      placeholder="Answer"
                      value={captcha.userAnswer}
                      onChange={handleCaptchaChange}
                      style={{
                        padding: '1rem',
                        border: `2px solid ${captcha.userAnswer ? (captcha.isValid ? '#2E7D32' : '#f44336') : '#e9ecef'}`,
                        borderRadius: '10px',
                        fontSize: '1.1rem',
                        fontWeight: '500',
                        width: '120px',
                        textAlign: 'center',
                        background: captcha.userAnswer ? (captcha.isValid ? '#e8f5e8' : '#ffebee') : 'white'
                      }}
                      required
                    />
                    
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      style={{
                        background: '#1565C0',
                        color: 'white',
                        border: 'none',
                        padding: '0.8rem 1rem',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#0D47A1'}
                      onMouseOut={(e) => e.target.style.background = '#1565C0'}
                    >
                      🔄 New
                    </button>
                  </div>
                  
                  {captcha.userAnswer && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      textAlign: 'center',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: captcha.isValid ? '#2E7D32' : '#f44336'
                    }}>
                      {captcha.isValid ? '✅ Correct!' : '❌ Incorrect, please try again'}
                    </div>
                  )}
                  
                  <div style={{ 
                    marginTop: '1rem', 
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    Please solve this simple math problem to verify you're human
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn modern-submit"
                  disabled={isSubmitting || !captcha.isValid}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner">⏳</span>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <span className="submit-icon">🚀</span>
                      Submit Application
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section career-contact">
        <div className="container">
          <h2 className="section-title fade-in" style={{ color: 'white' }}>Need Help with Your Application?</h2>
          <p className="section-subtitle fade-in" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Our HR team is here to assist you throughout the application process
          </p>
          
          <div className="contact-cards-grid fade-in">
            <div className="contact-card">
              <div className="contact-card-icon">✉️</div>
              <h4>Career Inquiries</h4>
              <p>contact@vr-eco.com</p>
              <small>We respond within 24 hours</small>
            </div>
            
            <div className="contact-card">
              <div className="contact-card-icon">📞</div>
              <h4>HR Helpline</h4>
              <p>+91 982 585 7777</p>
              <small>Mon-Sat: 9:00 AM - 6:00 PM</small>
            </div>
            
            <div className="contact-card">
              <div className="contact-card-icon">📍</div>
              <h4>Visit Our Office</h4>
              <p>O-5, Kanaknidhi Complex, Nanpura, Surat, Gujarat, India</p>
              <small>Schedule an appointment</small>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Career;